import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import logger from "../utils/logger";
import { FileText, Plus, Send, Clock, CheckCircle, AlertTriangle, Download, Loader2 } from "lucide-react";

const API = `${window.location.origin}/api`;

const STATUS_CFG = {
  draft: { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", label: "Draft", icon: Clock },
  under_review: { color: "#2563eb", bg: "rgba(37,99,235,0.12)", label: "Under Review", icon: AlertTriangle },
  filed: { color: "#10b981", bg: "rgba(16,185,129,0.12)", label: "Filed", icon: CheckCircle },
};

function STRForm({ onGenerated }) {
  const [form, setForm] = useState({ customer_name: "", amount: "", alert_type: "Suspicious transaction", transaction_summary: "", supporting_evidence: "" });
  const [generating, setGenerating] = useState(false);

  const handleSubmit = async () => {
    if (!form.customer_name) return;
    setGenerating(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      const { data } = await axios.post(`${API}/str/generate`, payload, { withCredentials: true });
      if (onGenerated) onGenerated(data);
      setForm({ customer_name: "", amount: "", alert_type: "Suspicious transaction", transaction_summary: "", supporting_evidence: "" });
    } catch (err) {
      logger.error("STR generation failed:", err);
    } finally {
      setGenerating(false);
    }
  };

  const inputStyle = {
    width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px",
    padding: "10px 14px", color: "#f1f5f9", fontSize: "13px", outline: "none", boxSizing: "border-box",
  };

  return (
    <div className="card-aml" data-testid="str-form">
      <div className="flex items-center gap-2 mb-4">
        <Plus style={{ width: "16px", height: "16px", color: "#2563eb" }} />
        <h2 style={{ fontSize: "14px", fontWeight: 700, color: "#f1f5f9" }}>Generate New STR</h2>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: "4px" }}>Customer Name *</label>
            <input value={form.customer_name} onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))}
              placeholder="Enter customer name..." style={inputStyle} data-testid="str-customer-input" />
          </div>
          <div>
            <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: "4px" }}>Amount (INR)</label>
            <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
              placeholder="0" style={inputStyle} data-testid="str-amount-input" />
          </div>
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: "4px" }}>Alert Type</label>
          <select value={form.alert_type} onChange={e => setForm(p => ({ ...p, alert_type: e.target.value }))}
            style={{ ...inputStyle, cursor: "pointer" }} data-testid="str-alert-type">
            <option>Suspicious transaction</option>
            <option>Cash threshold breach</option>
            <option>Structuring</option>
            <option>Round-trip transaction</option>
            <option>High-risk jurisdiction</option>
            <option>PEP match</option>
            <option>Sanctions match</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: "4px" }}>Transaction Summary</label>
          <textarea value={form.transaction_summary} onChange={e => setForm(p => ({ ...p, transaction_summary: e.target.value }))}
            placeholder="Brief summary of the suspicious transactions..." rows={2} style={{ ...inputStyle, resize: "vertical" }} data-testid="str-txn-summary" />
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: "4px" }}>Supporting Evidence</label>
          <textarea value={form.supporting_evidence} onChange={e => setForm(p => ({ ...p, supporting_evidence: e.target.value }))}
            placeholder="Additional evidence or observations..." rows={2} style={{ ...inputStyle, resize: "vertical" }} data-testid="str-evidence" />
        </div>
        <button onClick={handleSubmit} disabled={generating || !form.customer_name} data-testid="str-generate-btn" style={{
          width: "100%", background: generating ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
          border: "none", borderRadius: "8px", padding: "11px", cursor: generating ? "not-allowed" : "pointer",
          color: "#fff", fontSize: "13px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
        }}>
          {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating AI Narrative...</> : <><Send style={{ width: "14px", height: "14px" }} /> Generate STR with AI</>}
        </button>
      </div>
    </div>
  );
}

function STRList({ reports, onStatusChange }) {
  return (
    <div className="card-aml" data-testid="str-list" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #1e2530" }}>
        <div className="flex items-center gap-2">
          <FileText style={{ width: "16px", height: "16px", color: "#FF6B35" }} />
          <h2 style={{ fontSize: "14px", fontWeight: 700, color: "#f1f5f9" }}>STR Reports</h2>
          <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px", background: "rgba(255,107,53,0.12)", color: "#FF6B35" }}>
            {reports.length}
          </span>
        </div>
      </div>
      {reports.length === 0 ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#475569" }}>No STR reports yet. Generate one above.</div>
      ) : (
        <div>
          {reports.map(r => {
            const sc = STATUS_CFG[r.status] || STATUS_CFG.draft;
            const SIcon = sc.icon;
            return (
              <div key={r.id} data-testid={`str-${r.id}`} style={{ padding: "14px 20px", borderTop: "1px solid #0f1520" }}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#f1f5f9" }}>{r.customer_name}</span>
                    <span style={{ fontSize: "11px", color: "#475569", marginLeft: "8px" }}>{r.reference}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span style={{
                      fontSize: "9px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px",
                      background: sc.bg, color: sc.color, textTransform: "uppercase",
                      display: "flex", alignItems: "center", gap: "3px",
                    }}>
                      <SIcon style={{ width: "10px", height: "10px" }} /> {sc.label}
                    </span>
                    {r.status === "draft" && (
                      <button onClick={() => onStatusChange(r.id, "under_review")} style={{
                        background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.2)",
                        borderRadius: "5px", padding: "3px 8px", cursor: "pointer", color: "#60a5fa", fontSize: "10px", fontWeight: 600,
                      }}>Submit</button>
                    )}
                    {r.status === "under_review" && (
                      <button onClick={() => onStatusChange(r.id, "filed")} style={{
                        background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)",
                        borderRadius: "5px", padding: "3px 8px", cursor: "pointer", color: "#10b981", fontSize: "10px", fontWeight: 600,
                      }}>File to FIU</button>
                    )}
                  </div>
                </div>
                {r.narrative && (
                  <div style={{ fontSize: "11px", color: "#94a3b8", lineHeight: 1.6, maxHeight: "60px", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.narrative.slice(0, 200)}...
                  </div>
                )}
                <div className="flex items-center gap-4 mt-2" style={{ fontSize: "10px", color: "#475569" }}>
                  <span>{r.alert_type}</span>
                  {r.amount > 0 && <span style={{ fontWeight: 700, color: "#f1f5f9" }}>
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(r.amount)}
                  </span>}
                  <span>{new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function STRGeneratorPage() {
  const [reports, setReports] = useState([]);

  const fetchReports = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/str/list`, { withCredentials: true });
      setReports(data.reports || []);
    } catch (err) { logger.error("Failed to load STRs:", err); }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleGenerated = (newReport) => {
    setReports(prev => [newReport, ...prev]);
  };

  const handleStatusChange = async (strId, newStatus) => {
    try {
      await axios.put(`${API}/str/${strId}/status`, { status: newStatus }, { withCredentials: true });
      setReports(prev => prev.map(r => r.id === strId ? { ...r, status: newStatus } : r));
    } catch (err) { logger.error("Failed to update STR status:", err); }
  };

  return (
    <div data-testid="str-generator-page">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h1 style={{ fontSize: "26px", fontWeight: 700, letterSpacing: "-0.5px", color: "#f1f5f9" }} data-testid="str-generator-title">
            STR Generator
          </h1>
          <span style={{ fontSize: "9px", fontWeight: 700, padding: "3px 10px", borderRadius: "99px", background: "rgba(37,99,235,0.12)", color: "#60a5fa", textTransform: "uppercase" }}>
            AI-Powered
          </span>
        </div>
        <p style={{ color: "#94a3b8", fontSize: "14px" }}>Generate FIU-IND compliant Suspicious Transaction Reports</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <STRForm onGenerated={handleGenerated} />
        <STRList reports={reports} onStatusChange={handleStatusChange} />
      </div>
    </div>
  );
}
